import { useState, useEffect, useCallback, createContext, useContext, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type ExperienceMode = 'basics' | 'improve';

interface PreferredSportContextType {
  preferredSport: string;
  experienceMode: ExperienceMode;
  loaded: boolean;
  setSport: (sport: string) => Promise<void>;
  setExperienceMode: (mode: ExperienceMode) => Promise<void>;
  refetch: () => void;
}

const PreferredSportContext = createContext<PreferredSportContextType>({
  preferredSport: 'football',
  experienceMode: 'basics',
  loaded: false,
  setSport: async () => {},
  setExperienceMode: async () => {},
  refetch: () => {},
});

export function PreferredSportProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [preferredSport, setPreferredSport] = useState<string>('football');
  const [experienceMode, setExpMode] = useState<ExperienceMode>('basics');
  const [loaded, setLoaded] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  const refetch = useCallback(() => setFetchKey(k => k + 1), []);

  useEffect(() => {
    if (!user) {
      setPreferredSport('football');
      setExpMode('basics');
      setLoaded(false);
      return;
    }

    supabase
      .from('profiles')
      .select('preferred_sport, knowledge_level')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          if ((data as any).preferred_sport) {
            setPreferredSport((data as any).preferred_sport);
          }
          // Map knowledge_level to experience mode
          const kl = (data as any).knowledge_level;
          if (kl === 'intermediate' || kl === 'advanced') {
            setExpMode('improve');
          } else {
            setExpMode('basics');
          }
        }
        setLoaded(true);
      });
  }, [user, fetchKey]);

  const setSport = useCallback(async (sport: string) => {
    if (!user) return;
    setPreferredSport(sport);
    await supabase
      .from('profiles')
      .update({ preferred_sport: sport })
      .eq('user_id', user.id);
  }, [user]);

  const setExperienceMode = useCallback(async (mode: ExperienceMode) => {
    if (!user) return;
    setExpMode(mode);
    const knowledgeLevel = mode === 'improve' ? 'intermediate' : 'beginner';
    await supabase
      .from('profiles')
      .update({ knowledge_level: knowledgeLevel })
      .eq('user_id', user.id);
  }, [user]);

  return (
    <PreferredSportContext.Provider value={{ preferredSport, experienceMode, loaded, setSport, setExperienceMode, refetch }}>
      {children}
    </PreferredSportContext.Provider>
  );
}

export function usePreferredSport() {
  return useContext(PreferredSportContext);
}
